/**
 * Lobby / matchmaking / signaling / relay / ranked / result protocol (ARCHITECTURE §7).
 *
 * Transport: one WebSocket per client at `ws(s)://<host>/ws`.
 *   - TEXT frames  = JSON messages validated with the zod schemas below (`type` is the discriminator).
 *   - BINARY frames = opaque game packets (see ./packet.ts). In relay mode the server forwards every binary frame
 *     from one peer of a match to the other, unchanged. Outside relay mode binary frames are dropped.
 *
 * Validation: the server validates every inbound message with `ClientMessageSchema`; the client validates every
 * inbound message with `ServerMessageSchema`. Never trust the other side. zod is NEVER used inside the sim.
 * The schemas use `zod/mini` (the tree-shakeable functional API): this module sits in the client's first-paint
 * chunk, where classic zod cost ~25 kB gzip. Write checks as `z.string().check(z.maxLength(64))`, wrappers as
 * `z.optional(x)` — never the chained classic methods.
 *
 * Flow (online match):
 *   C hello{protocol, clientVersion, contentHash, platform?, appVersion?} → S welcome
 *     (S error{protocol_mismatch} + close 4000 if the protocol differs; S error{update_required} + close 4001 if the
 *      contentHash is not one the server accepts — legacy clients that send no `platform` get `version_mismatch`)
 *   C queue_join{mode} → S queue_status… → S match_found{matchId, you, config, polite, iceServers}
 *     (private rooms: room_create/room_join → room_state… → both room_ready → match_found)
 *   both peers exchange `signal` (WebRTC perfect negotiation; `polite` decides who yields on glare)
 *   if P2P fails within the timeout: C relay_request → S relay_ready (to both) → binary frames are relayed
 *   C match_ready (transport up) ×2 → S match_start{startAt}
 *   … game packets …
 *   C result_report ×2 → S match_result (+ rating deltas for ranked)
 *   rematch (same opponent, same transport): C rematch{matchId, want} ×2 → S rematch_state… → when both want it,
 *     S match_found{SAME matchId, rematch: true, fresh seed/config} → the clients KEEP the open transport (no new
 *     signaling/ICE) and recreate the sim → C match_ready ×2 → S match_start. Either side leaving or sending
 *     want:false ends the offer (rematch_state with declined: true).
 *   lobby socket drop mid-match: the server holds the seat MATCH_RESUME_GRACE_MS; the client reconnects with
 *     C hello{token, match} → S welcome{match} (+ relay_ready if relayed) and the game simply continues.
 *
 * Settings: quick/ranked queues use server-decided settings (ranked = rankedMatchConfig: drives, normalized
 * stats, specials OFF, no wind). Private rooms carry host-chosen `settings` (room_create, echoed in room_state).
 *
 * Build skew (cross-play): store builds lag web deploys and every tuning change alters SIM_CONTENT_HASH, so the server
 * accepts an allowlist of recent hashes (its own always included) and PARTITIONS by hash: queues only pair equal
 * hashes, and a private room only admits clients with the host's hash (`build_mismatch` otherwise). Every match is
 * therefore between two identical sim builds. The server builds match configs from its own @hf/shared, so a hash
 * may only stay on the allowlist while the wire shapes (character ids, MatchConfig ranges, room settings) are
 * compatible; anything else is a PROTOCOL_VERSION bump.
 */
import * as z from 'zod/mini';
import { CHARACTER_IDS } from './characters';
import { MatchFormat, SpecialsMode } from './enums';
import { PHYSICS } from './tuning';

/** Wire-format version (JSON + binary layouts). Gameplay data compatibility is SIM_CONTENT_HASH (./content.ts). */
export const PROTOCOL_VERSION = 2;
export const DEFAULT_SERVER_PORT = 8787;
export const WS_PATH = '/ws';
export const HEALTH_PATH = '/health';

/** Max size of a text message (bytes). Larger messages are rejected. */
export const MAX_TEXT_MESSAGE_BYTES = 16 * 1024;
/** Max size of a binary game packet (bytes). */
export const MAX_BINARY_MESSAGE_BYTES = 1024;
/** Server → client WebSocket ping interval; a client silent for HEARTBEAT_TIMEOUT_MS is dropped. */
export const HEARTBEAT_INTERVAL_MS = 10_000;
export const HEARTBEAT_TIMEOUT_MS = 30_000;
/** Token bucket per connection for TEXT messages. Binary relay frames have their own larger bucket. */
export const RATE_LIMIT_TEXT = { burst: 30, perSecond: 10 } as const;
export const RATE_LIMIT_BINARY = { burst: 240, perSecond: 180 } as const;
/** Give up on P2P and request relay after this long without an open DataChannel. */
export const P2P_CONNECT_TIMEOUT_MS = 5_000;
/** match_start.startAt is this far in the future (server clock) so both peers start together. */
export const MATCH_START_DELAY_MS = 1_500;
/**
 * A started match keeps a player's seat this long after their lobby socket dropped (Wi-Fi roam, proxy idle drop):
 * a resumed hello with `match` re-attaches it, otherwise it is a forfeit when the window ends. Longer than the
 * netcode's peer timeout, so a relayed game that is really dead ends on the clients first.
 */
export const MATCH_RESUME_GRACE_MS = 20_000;

/** Max |wind| the server may choose (yd/s²). */
export const MAX_WIND = PHYSICS.maxWind;

// ── Elo ────────────────────────────────────────────────────────────────────────────────────────────────
export const ELO_K = 32;
export const ELO_START = 1000;
/** Expected score of A vs B. Pure; used by the server and for UI previews. */
export const eloExpected = (ratingA: number, ratingB: number): number =>
  1 / (1 + 10 ** ((ratingB - ratingA) / 400));
/** New rating for A after a game (scoreA: 1 win, 0 loss, 0.5 draw). Rounded to an integer. */
export const eloUpdate = (ratingA: number, ratingB: number, scoreA: number, k: number = ELO_K): number =>
  Math.round(ratingA + k * (scoreA - eloExpected(ratingA, ratingB)));

// ── Primitives ─────────────────────────────────────────────────────────────────────────────────────────
export const PlayerNameSchema = z
  .string()
  .check(
    z.trim(),
    z.minLength(1),
    z.maxLength(16),
    z.regex(/^[\p{L}\p{N} _.\-!?]+$/u, 'letters, numbers, space and _ . - ! ? only'),
  );
export const PlayerIdSchema = z.string().check(z.minLength(1), z.maxLength(64));
export const MatchIdSchema = z.string().check(z.minLength(1), z.maxLength(64));
export const RoomCodeSchema = z.string().check(z.trim(), z.toUpperCase(), z.regex(/^[A-Z]{4}$/, '4 letters'));
export const CharacterIdSchema = z.enum(CHARACTER_IDS);
export const QueueModeSchema = z.enum(['quick', 'ranked']);
export type QueueMode = z.infer<typeof QueueModeSchema>;
export const MatchKindSchema = z.enum(['quick', 'ranked', 'private']);
export type MatchKind = z.infer<typeof MatchKindSchema>;
export const PlayerIndexSchema = z.union([z.literal(0), z.literal(1)]);
const u32 = z.int().check(z.gte(0), z.lte(0xffffffff));

export const MatchFormatSchema = z.union([z.literal(MatchFormat.DRIVES), z.literal(MatchFormat.CLASSIC)]);
export const SpecialsModeSchema = z.union([
  z.literal(SpecialsMode.OFF),
  z.literal(SpecialsMode.SIGNATURE),
  z.literal(SpecialsMode.STANDARD),
]);
export const DrivesPerPlayerSchema = z.int().check(z.gte(1), z.lte(10));
export const TimeOfDaySchema = z.enum(['day', 'night']);

export const MatchConfigSchema = z.object({
  seed: u32,
  format: MatchFormatSchema,
  characters: z.tuple([CharacterIdSchema, CharacterIdSchema]),
  normalizedStats: z.boolean(),
  specials: SpecialsModeSchema,
  drivesPerPlayer: DrivesPerPlayerSchema,
  firstPossession: PlayerIndexSchema,
  wind: z.number().check(z.gte(-MAX_WIND), z.lte(MAX_WIND)),
  /** Cosmetic stadium lighting (optional: omitted = derived from the seed, see `timeOfDayOf`). */
  timeOfDay: z.optional(TimeOfDaySchema),
});

/** Host-chosen settings of a private room. */
export const RoomSettingsSchema = z.object({
  format: MatchFormatSchema,
  specials: SpecialsModeSchema,
  drivesPerPlayer: DrivesPerPlayerSchema,
  /** Stadium lighting chosen by the host (optional: omitted = derived from each game's seed). */
  timeOfDay: z.optional(TimeOfDaySchema),
});
export type RoomSettings = z.infer<typeof RoomSettingsSchema>;

export const RatingInfoSchema = z.object({
  rating: z.int(),
  games: z.int().check(z.gte(0)),
  wins: z.int().check(z.gte(0)),
  losses: z.int().check(z.gte(0)),
});
export type RatingInfo = z.infer<typeof RatingInfoSchema>;

/** WebRTC signaling payload (perfect negotiation: descriptions + trickle ICE). */
export const SignalDataSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('description'),
    type: z.enum(['offer', 'answer', 'pranswer', 'rollback']),
    sdp: z.optional(z.string().check(z.maxLength(12_000))),
  }),
  z.object({
    kind: z.literal('candidate'),
    candidate: z.string().check(z.maxLength(2_000)),
    sdpMid: z.optional(z.nullable(z.string().check(z.maxLength(64)))),
    sdpMLineIndex: z.optional(z.nullable(z.int().check(z.gte(0), z.lte(64)))),
    usernameFragment: z.optional(z.nullable(z.string().check(z.maxLength(256)))),
  }),
  /** End of candidates. */
  z.object({ kind: z.literal('candidate_end') }),
]);
export type SignalData = z.infer<typeof SignalDataSchema>;

export const IceServerSchema = z.object({
  urls: z.union([z.string(), z.array(z.string())]),
  username: z.optional(z.string()),
  credential: z.optional(z.string()),
});
export type IceServerConfig = z.infer<typeof IceServerSchema>;

/** Where a client runs (hello.platform). Absent = a client from before the tag existed (web). */
export const ClientPlatformSchema = z.enum(['web', 'ios', 'android']);
export type ClientPlatform = z.infer<typeof ClientPlatformSchema>;

export const ErrorCodeSchema = z.enum([
  'bad_message',
  'protocol_mismatch',
  /**
   * hello.contentHash is not accepted by the server: the client runs a different sim build. Sent to legacy clients
   * (no hello.platform); newer clients get `update_required`.
   */
  'version_mismatch',
  /**
   * hello.contentHash is not on the server's allowlist of recent builds: reload the page (web) or update the app
   * (ios / android). The message says which. Followed by close 4001.
   */
  'update_required',
  /** room_join: the room's host runs a different game build (content hash), so the two cannot play each other. */
  'build_mismatch',
  'rate_limited',
  'not_welcomed',
  'already_queued',
  'already_in_match',
  'room_not_found',
  'room_full',
  'not_in_room',
  'not_in_match',
  'internal',
]);
export type ErrorCode = z.infer<typeof ErrorCodeSchema>;

export const LeaveReasonSchema = z.enum(['quit', 'disconnect', 'timeout', 'desync', 'error', 'version']);
export type LeaveReason = z.infer<typeof LeaveReasonSchema>;

// ── Client → Server ────────────────────────────────────────────────────────────────────────────────────
export const HelloMsg = z.object({
  type: z.literal('hello'),
  protocol: z.int(),
  /** Preferred display name (server may sanitize/assign one). */
  name: z.optional(PlayerNameSchema),
  /** Session token from a previous `welcome` to keep the same player id / rating. */
  token: z.optional(z.string().check(z.maxLength(128))),
  /** Client build id (package version + git sha); logged by the server, shown in error reports. */
  clientVersion: z.string().check(z.minLength(1), z.maxLength(64)),
  /**
   * SIM_CONTENT_HASH of the client's build. Must be on the server's allowlist (its own hash plus recent builds);
   * the client is only ever paired with clients of the same hash.
   */
  contentHash: u32,
  /** Where the client runs (logged, stored on the match, recorded with ranked results). Omitted = legacy web. */
  platform: z.optional(ClientPlatformSchema),
  /** Native shell version (store build, e.g. "1.2.0 (42)"); absent on web. `clientVersion` names the JS bundle. */
  appVersion: z.optional(z.string().check(z.minLength(1), z.maxLength(64))),
  /**
   * Reconnect mid-match: the match this client is STILL running (its game kept going over P2P, or its relay will
   * resume). The server holds a started match's seat for MATCH_RESUME_GRACE_MS after a socket drop; a resumed
   * hello that names it re-attaches the seat (welcome.match echoes it). Omitted = the client gave the match up
   * (an unclaimed held seat is forfeited at once).
   */
  match: z.optional(MatchIdSchema),
});
export const SetNameMsg = z.object({ type: z.literal('set_name'), name: PlayerNameSchema });
export const QueueJoinMsg = z.object({
  type: z.literal('queue_join'),
  mode: QueueModeSchema,
  character: CharacterIdSchema,
});
export const QueueLeaveMsg = z.object({ type: z.literal('queue_leave') });
export const RoomCreateMsg = z.object({
  type: z.literal('room_create'),
  character: CharacterIdSchema,
  /** Omitted = casual defaults (drives, signature specials, DRIVES_PER_PLAYER). */
  settings: z.optional(RoomSettingsSchema),
});
export const RoomJoinMsg = z.object({
  type: z.literal('room_join'),
  code: RoomCodeSchema,
  character: CharacterIdSchema,
});
export const RoomLeaveMsg = z.object({ type: z.literal('room_leave') });
export const RoomReadyMsg = z.object({
  type: z.literal('room_ready'),
  ready: z.boolean(),
  character: CharacterIdSchema,
});
export const SignalOutMsg = z.object({
  type: z.literal('signal'),
  matchId: MatchIdSchema,
  data: SignalDataSchema,
});
export const RelayRequestMsg = z.object({ type: z.literal('relay_request'), matchId: MatchIdSchema });
/** The client has its transport open and its sim created; the server answers with match_start once both are ready. */
export const MatchReadyMsg = z.object({ type: z.literal('match_ready'), matchId: MatchIdSchema });
export const ResultReportMsg = z.object({
  type: z.literal('result_report'),
  matchId: MatchIdSchema,
  winner: z.union([PlayerIndexSchema, z.literal(-1)]),
  score: z.tuple([z.int().check(z.gte(0)), z.int().check(z.gte(0))]),
  ticks: u32,
  finalHash: u32,
});
/** Offer (want: true) or decline/withdraw (want: false) a rematch after match_result. */
export const RematchMsg = z.object({ type: z.literal('rematch'), matchId: MatchIdSchema, want: z.boolean() });
export const MatchLeaveMsg = z.object({
  type: z.literal('match_leave'),
  matchId: MatchIdSchema,
  reason: LeaveReasonSchema,
});
export const RatingGetMsg = z.object({ type: z.literal('rating_get') });
export const LeaderboardGetMsg = z.object({
  type: z.literal('leaderboard_get'),
  limit: z._default(z.int().check(z.gte(1), z.lte(100)), 20),
});
export const PingMsg = z.object({ type: z.literal('ping'), id: u32, clientTime: z.number() });
/** Guideline 1.2: report a player (by lobby player id from match_found / leaderboard). Signed-in accounts only. */
export const PlayerReportReasonSchema = z.enum(['cheating', 'offensive_name', 'harassment', 'other']);
export const PlayerReportMsg = z.object({
  type: z.literal('player_report'),
  playerId: PlayerIdSchema,
  reason: PlayerReportReasonSchema,
  matchId: z.optional(MatchIdSchema),
});
/** Block (or unblock) a player: hidden from your leaderboard, never matched with you again. */
export const PlayerBlockMsg = z.object({
  type: z.literal('player_block'),
  playerId: PlayerIdSchema,
  unblock: z.optional(z.boolean()),
});

export const ClientMessageSchema = z.discriminatedUnion('type', [
  HelloMsg,
  SetNameMsg,
  QueueJoinMsg,
  QueueLeaveMsg,
  RoomCreateMsg,
  RoomJoinMsg,
  RoomLeaveMsg,
  RoomReadyMsg,
  SignalOutMsg,
  RelayRequestMsg,
  MatchReadyMsg,
  ResultReportMsg,
  RematchMsg,
  MatchLeaveMsg,
  RatingGetMsg,
  LeaderboardGetMsg,
  PingMsg,
  PlayerReportMsg,
  PlayerBlockMsg,
]);
export type ClientMessage = z.infer<typeof ClientMessageSchema>;
/** Input type (before defaults are applied), for building messages on the client. */
export type ClientMessageInput = z.input<typeof ClientMessageSchema>;
export type ClientMessageType = ClientMessage['type'];

// ── Server → Client ────────────────────────────────────────────────────────────────────────────────────
export const WelcomeMsg = z.object({
  type: z.literal('welcome'),
  protocol: z.int(),
  playerId: PlayerIdSchema,
  name: z.string(),
  /** Opaque session token; store it and send it in the next `hello`. */
  token: z.string(),
  rating: RatingInfoSchema,
  serverTime: z.number(),
  /**
   * The match claimed in `hello.match` was re-attached to this connection: keep running it (the server re-sends
   * relay_ready if it is relayed; its verdicts arrive here). Absent = no such match any more: drop it (its verdict,
   * if it was settled meanwhile, still follows as match_result).
   */
  match: z.optional(MatchIdSchema),
});
export const ErrorMsg = z.object({
  type: z.literal('error'),
  /** An unknown code (a newer server) decodes as `internal` with its message kept, instead of dropping the error. */
  code: z.catch(ErrorCodeSchema, 'internal'),
  message: z.string(),
  /** The `type` of the request that caused it, if any. */
  ref: z.optional(z.string()),
});
export const NameSetMsg = z.object({ type: z.literal('name_set'), name: z.string() });
export const QueueStatusMsg = z.object({
  type: z.literal('queue_status'),
  mode: QueueModeSchema,
  state: z.enum(['searching', 'idle']),
  waitingMs: z.int().check(z.gte(0)),
  playersInQueue: z.int().check(z.gte(0)),
});
export const RoomPlayerSchema = z.object({
  id: PlayerIdSchema,
  name: z.string(),
  character: CharacterIdSchema,
  ready: z.boolean(),
  rating: z.int(),
});
export type RoomPlayer = z.infer<typeof RoomPlayerSchema>;
export const RoomStateMsg = z.object({
  type: z.literal('room_state'),
  code: RoomCodeSchema,
  hostId: PlayerIdSchema,
  players: z.array(RoomPlayerSchema).check(z.maxLength(2)),
  status: z.enum(['waiting', 'starting']),
  settings: RoomSettingsSchema,
});
export const RoomClosedMsg = z.object({
  type: z.literal('room_closed'),
  code: RoomCodeSchema,
  reason: z.string(),
});
export const MatchPlayerSchema = z.object({
  id: PlayerIdSchema,
  name: z.string(),
  character: CharacterIdSchema,
  rating: z.int(),
});
export type MatchPlayer = z.infer<typeof MatchPlayerSchema>;
export const MatchFoundMsg = z.object({
  type: z.literal('match_found'),
  matchId: MatchIdSchema,
  kind: MatchKindSchema,
  /** Your player index in the sim (0 = HOME). */
  you: PlayerIndexSchema,
  players: z.tuple([MatchPlayerSchema, MatchPlayerSchema]),
  /** Both peers MUST create the sim from exactly this config. */
  config: MatchConfigSchema,
  /** Perfect negotiation role: the polite peer yields on offer glare. The impolite peer makes the first offer. */
  polite: z.boolean(),
  iceServers: z.array(IceServerSchema),
  /** A rematch of `matchId`: keep the existing transport, skip signaling, recreate the sim from `config`. */
  rematch: z.optional(z.boolean()),
});
export const SignalInMsg = z.object({
  type: z.literal('signal'),
  matchId: MatchIdSchema,
  data: SignalDataSchema,
});
export const RelayReadyMsg = z.object({ type: z.literal('relay_ready'), matchId: MatchIdSchema });
/** Rematch offer state for both players (after any `rematch` message or when an offer lapses). */
export const RematchStateMsg = z.object({
  type: z.literal('rematch_state'),
  matchId: MatchIdSchema,
  /** You have offered. */
  you: z.boolean(),
  /** The opponent has offered. */
  opponent: z.boolean(),
  /** The offer is dead (someone declined or left). */
  declined: z.boolean(),
});
export const MatchStartMsg = z.object({
  type: z.literal('match_start'),
  matchId: MatchIdSchema,
  /** Server-clock ms at which tick 0 should run. Clients convert with their ping-estimated clock offset. */
  startAt: z.number(),
});
export const OpponentLeftMsg = z.object({
  type: z.literal('opponent_left'),
  matchId: MatchIdSchema,
  reason: LeaveReasonSchema,
});
export const RatingChangeSchema = z.object({
  before: z.int(),
  after: z.int(),
  delta: z.int(),
});
export const MatchResultMsg = z.object({
  type: z.literal('match_result'),
  matchId: MatchIdSchema,
  /** accepted = both reports agreed; disputed = they disagreed (no rating change); forfeit = opponent left. */
  status: z.enum(['accepted', 'disputed', 'forfeit']),
  winner: z.union([PlayerIndexSchema, z.literal(-1)]),
  score: z.tuple([z.int(), z.int()]),
  /** Ranked only. */
  rating: z.optional(RatingChangeSchema),
});
export const RatingMsg = z.object({ type: z.literal('rating'), rating: RatingInfoSchema });
export const LeaderboardEntrySchema = z.object({
  rank: z.int(),
  name: z.string(),
  rating: z.int(),
  games: z.int(),
  /** Lobby player id (for Report / Block). Absent from older servers. */
  id: z.optional(PlayerIdSchema),
});
/** Outcome of player_report / player_block. */
export const PlayerActionMsg = z.object({
  type: z.literal('player_action'),
  action: z.enum(['report', 'block', 'unblock']),
  playerId: PlayerIdSchema,
  ok: z.boolean(),
  /** Why it failed: sign_in_required | unknown_player | self | rate_limited | unavailable. */
  error: z.optional(z.string()),
});
export const LeaderboardMsg = z.object({
  type: z.literal('leaderboard'),
  entries: z.array(LeaderboardEntrySchema),
});
export const PongMsg = z.object({
  type: z.literal('pong'),
  id: u32,
  clientTime: z.number(),
  serverTime: z.number(),
});

export const ServerMessageSchema = z.discriminatedUnion('type', [
  WelcomeMsg,
  ErrorMsg,
  NameSetMsg,
  QueueStatusMsg,
  RoomStateMsg,
  RoomClosedMsg,
  MatchFoundMsg,
  SignalInMsg,
  RelayReadyMsg,
  RematchStateMsg,
  MatchStartMsg,
  OpponentLeftMsg,
  MatchResultMsg,
  RatingMsg,
  LeaderboardMsg,
  PlayerActionMsg,
  PongMsg,
]);
export type ServerMessage = z.infer<typeof ServerMessageSchema>;
export type ServerMessageType = ServerMessage['type'];

/** Narrow a message union by its `type`. */
export type MessageOf<M extends { type: string }, T extends M['type']> = Extract<M, { type: T }>;

// ── Encode / decode helpers ────────────────────────────────────────────────────────────────────────────
export const encodeMessage = (msg: ClientMessageInput | ServerMessage): string => JSON.stringify(msg);

export type DecodeResult<M> = { ok: true; msg: M } | { ok: false; error: string };

const decodeWith = <M>(schema: z.ZodMiniType<M>, raw: string): DecodeResult<M> => {
  if (raw.length > MAX_TEXT_MESSAGE_BYTES) return { ok: false, error: 'message too large' };
  let json: unknown;
  try {
    json = JSON.parse(raw);
  } catch {
    return { ok: false, error: 'invalid JSON' };
  }
  const r = schema.safeParse(json);
  return r.success
    ? { ok: true, msg: r.data }
    : {
        ok: false,
        error: r.error.issues.map((i) => `${i.path.join('.') || '(root)'}: ${describeIssue(i)}`).join('; '),
      };
};

/**
 * zod/mini ships no locale (every default message is "Invalid input"), so describe issues from their data.
 * Custom messages (e.g. the player-name regex) are kept.
 */
const describeIssue = (i: z.core.$ZodIssue): string => {
  if (i.message && i.message !== 'Invalid input') return i.message;
  switch (i.code) {
    case 'invalid_type':
      return `expected ${i.expected}`;
    case 'too_big':
      return `too big (max ${String(i.maximum)})`;
    case 'too_small':
      return `too small (min ${String(i.minimum)})`;
    case 'invalid_value':
      return `expected one of ${i.values.map((v) => JSON.stringify(v)).join(', ')}`;
    case 'invalid_union':
      return 'unknown or invalid variant';
    case 'invalid_format':
      return `invalid ${i.format}`;
    default:
      return i.code.replace(/_/g, ' ');
  }
};

/** Server side: parse + validate an inbound text frame. */
export const decodeClientMessage = (raw: string): DecodeResult<ClientMessage> =>
  decodeWith(ClientMessageSchema, raw);
/** Client side: parse + validate an inbound text frame. */
export const decodeServerMessage = (raw: string): DecodeResult<ServerMessage> =>
  decodeWith(ServerMessageSchema, raw);
